import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Header } from '@/components/layout/Header';
import { useLanguage } from '@/contexts/LanguageContext';
import { CheckCircle2, Home } from 'lucide-react';

type StoredCartItem = {
  product: {
    id: string;
    name: string;
    price: number;
    image: string;
    description: string;
  };
  quantity: number;
};

export default function ConfirmationPage() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { hotelId } = useParams<{ hotelId: string }>();
  const [orderId, setOrderId] = useState<string | null>(null);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    // Retrieve order details from session storage
    const storedOrderId = sessionStorage.getItem('lastOrderId');
    const storedOrderItems = sessionStorage.getItem('lastOrderItems');
    
    if (!storedOrderId || !storedOrderItems) {
      navigate(`/${hotelId}`);
      return;
    }
    
    setOrderId(storedOrderId);
    
    try {
      const parsedItems = JSON.parse(storedOrderItems) as StoredCartItem[];
      
      // Calculate total
      const total = parsedItems.reduce(
        (sum, item) => sum + (item.product.price * item.quantity), 
        0
      );
      setTotalAmount(total);
      
    } catch (error) {
      console.error('Error parsing order items:', error);
      navigate(`/${hotelId}`);
    }
  }, [hotelId, navigate]);

  if (!orderId) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header showBackButton={false} title={t('orderConfirmed')} showCart={false} />
      
      <main className="flex-1 overflow-y-auto py-4 px-4 hide-scrollbar">
        <div className="max-w-md mx-auto text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-green-100 mb-4">
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          </div>
          
          <h1 className="text-xl font-bold mb-2">
            {t('orderConfirmed')}
          </h1>
          
          <p className="text-muted-foreground mb-4">
            {t('thankYou')}
          </p>
          
          <div className="border rounded-lg p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-muted-foreground">{t('orderNumber')}</span>
              <span className="font-medium">{orderId}</span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('total')}</span>
              <span className="font-bold">${totalAmount.toFixed(2)}</span>
            </div>
          </div>
          
          <Button 
            onClick={() => navigate(`/${hotelId}`)}
            className="w-full"
            size="lg"
          >
            <Home className="mr-2 h-4 w-4" />
            {t('backToHome')}
          </Button>
        </div>
      </main>
    </div>
  );
}